import { useState, useMemo } from "react";
import { Code, FileText } from "lucide-react";
import { AppShell } from "../layouts/AppShell";

export type EmailTemplateRecord = {
  id: string;
  code: string;
  description: string;
  processReferences: string[];
};

// Template bidon pour développement et tests
const DUMMY_TEMPLATES: EmailTemplateRecord[] = [
  {
    id: "demo-1",
    code: "DEMO_STEP_ALERT",
    description: "Template bidon pour tests et développement (alertes d'étape).",
    processReferences: [],
  },
];

type AdminEmailTemplatesPageProps = {
  userName?: string;
};

const adminModules = [
  { label: "Administration", sectionHeader: true, separator: true },
  { label: "Utilisateurs", onClick: () => (window.location.hash = "#/admin") },
  { label: "Services et Produits", onClick: () => (window.location.hash = "#/admin-products") },
  { label: "Promotions", onClick: () => (window.location.hash = "#/admin-promotions") },
  { label: "Processus", onClick: () => (window.location.hash = "#/admin-processes") },
  { label: "Templates de courriel", onClick: () => (window.location.hash = "#/admin-email-templates") },
  { label: "Facturation", onClick: () => (window.location.hash = "#/admin-billing") },
];

export function AdminEmailTemplatesPage({ userName }: AdminEmailTemplatesPageProps) {
  const [templates] = useState<EmailTemplateRecord[]>(DUMMY_TEMPLATES);

  return (
    <AppShell
      topbar={<div className="text-sm text-muted">Administration — Templates de courriel</div>}
      userName={userName}
      modules={adminModules}
      userMenuItems={[
        { label: "Profil", onClick: () => (window.location.hash = "#/profile") },
        { label: "Paramètres", onClick: () => (window.location.hash = "#/settings") },
        { label: "Déconnexion", onClick: () => {} },
      ]}
    >
      <div className="space-y-6">
        <div>
          <h1 className="text-section text-text">Templates de courriel</h1>
          <p className="text-sm text-muted mt-1">
            Liste des modèles de courriel utilisables dans les processus (triggers, rappels, notifications).
            Chaque template est identifié par un code et peut être référencé par un ou plusieurs processus.
          </p>
        </div>

        <div className="rounded-lg border border-border bg-surface overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-surface-muted/50">
                <th className="text-left px-4 py-3 font-medium text-text flex items-center gap-2">
                  <Code size={14} />
                  Code
                </th>
                <th className="text-left px-4 py-3 font-medium text-text flex items-center gap-2">
                  <FileText size={14} />
                  Description
                </th>
                <th className="text-left px-4 py-3 font-medium text-text">
                  Processus en référence
                </th>
              </tr>
            </thead>
            <tbody>
              {templates.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-4 py-8 text-center text-muted">
                    Aucun template configuré.
                  </td>
                </tr>
              ) : (
                templates.map((t) => (
                  <tr key={t.id} className="border-b border-border last:border-0 hover:bg-surface-muted/30">
                    <td className="px-4 py-3">
                      <code className="text-xs font-mono bg-surface-muted px-2 py-1 rounded">
                        {t.code}
                      </code>
                    </td>
                    <td className="px-4 py-3 text-text">{t.description}</td>
                    <td className="px-4 py-3 text-muted">
                      {t.processReferences.length === 0
                        ? "—"
                        : t.processReferences.join(", ")}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-muted">
          La gestion complète (création, édition, liaison aux processus) sera ajoutée lorsque l’API
          des templates de courriel sera disponible. Pour l’instant, un template bidon (
          <code className="bg-surface-muted px-1 rounded">DEMO_STEP_ALERT</code>) est disponible pour
          les tests.
        </p>
      </div>
    </AppShell>
  );
}
